// Delivery rules as the admin edits them since tasks#211: "who delivers" per time window, either
// Ordit's couriers (an ordered method list, the first is the primary) or the restaurant itself
// (one restaurant_managed method). The "Ordit manages delivery" switch is gone: it is always on,
// and "the restaurant delivers" is the restaurant_managed method — core-api creates no delivery
// job for either (DeliveryRuleSelector / DeliveryManager::deliveryIsRequired).

export const RESTAURANT_MANAGED = 'restaurant_managed'

// what a new restaurant (and a window switched back to Ordit couriers) starts with (user, 2026-10-30)
export const DEFAULT_COURIERS = ['stuart', 'onfleet', 'ordit_dispatch']

// never offered for a new pick: unused (tasks#211) or manual only
export const HIDDEN_COURIERS = [
    RESTAURANT_MANAGED,
    'auto_dispatch',
    'onfleet_pedivan',
]

export const DAY_START = '00:00'
export const DAY_END = '23:59'

const DAY_MINUTES = 24 * 60

// the API sends a datetime for the TIME column ("1970-01-01T14:59:00+01:00"), the editor HH:mm;
// the wall-clock part is cut out as it is (no timezone conversion, like the other admin dates)
export function ruleTime(value) {
    if (!value) return null
    const text = String(value)
    const fromDateTime = text.match(/T(\d{2}:\d{2})/)
    if (fromDateTime) return fromDateTime[1]
    const plain = text.match(/^(\d{2}:\d{2})/)
    return plain ? plain[1] : null
}

export function toMinutes(time) {
    const hhmm = ruleTime(time)
    if (!hhmm) return null
    const [h, m] = hhmm.split(':').map((part) => parseInt(part, 10))
    return h * 60 + m
}

export function fromMinutes(minutes) {
    const clamped = Math.max(0, Math.min(DAY_MINUTES - 1, minutes))
    const h = String(Math.floor(clamped / 60)).padStart(2, '0')
    const m = String(clamped % 60).padStart(2, '0')
    return `${h}:${m}`
}

export function newDeliveryRule(
    timeFrom = DAY_START,
    timeTo = DAY_END,
    methods = DEFAULT_COURIERS
) {
    return {
        '@type': 'DeliveryRule',
        // not edited: distance and delivery unit 0 - empty, one motorcycle (every prod rule, tasks#211)
        distanceFrom: 0,
        distanceTo: null,
        deliveryUnitFrom: 0,
        deliveryUnitTo: null,
        vehicleType: 'motorcycle',
        numberOfVehicle: 1,
        thermalBagNeeded: false,
        thermalBoxNeeded: false,
        timeFrom,
        timeTo,
        deliveryMethods: methods.map((method) => ({ method, backup: false })),
    }
}

export function isRestaurantManaged(rule) {
    return (rule?.deliveryMethods || []).some(
        (item) => item.method === RESTAURANT_MANAGED
    )
}

export function courierKeys(rule) {
    return (rule?.deliveryMethods || [])
        .map((item) => item.method)
        .filter((method) => method && method !== RESTAURANT_MANAGED)
}

// Writes an ordered method list into a rule. The stored items keep their @id by position (only
// their `method` changes), so a reorder updates rows in place: core-api reads them in id order and
// the first one is the primary.
export function setRuleMethods(rule, methods) {
    const items = rule.deliveryMethods || []
    methods.forEach((method, index) => {
        if (items[index]) {
            items[index].method = method
            items[index].backup = false
        } else {
            items.push({ method, backup: false })
        }
    })
    items.splice(methods.length)
    rule.deliveryMethods = items
}

// true when Ordit requests a courier in at least one window: the prep time and the pick up
// address are needed then (they used to follow the "Ordit manages delivery" switch)
export function usesOrditCouriers(deliverySetting) {
    return (deliverySetting?.deliveryRules || []).some(
        (rule) => !isRestaurantManaged(rule)
    )
}

const methodId = (item) =>
    item?.['@id']
        ? parseInt(String(item['@id']).split('/').pop(), 10)
        : Number.MAX_SAFE_INTEGER

// fromEntity: a setting whose switch is off behaves as "the restaurant delivers" in every window
// (no delivery job), so it is shown and saved that way, with the switch on. Done in the mapper,
// not in a component, so an untouched page stays clean.
export function normalizeDeliverySetting(deliverySetting) {
    if (!deliverySetting)
        return // the API does not keep the stored order of a rule's methods (restaurant 300 locally came back
        // 19, 18), core-api reads them in id order: sort by id so "first = primary" is what it uses
    ;(deliverySetting.deliveryRules || []).forEach((rule) => {
        if (Array.isArray(rule.deliveryMethods)) {
            rule.deliveryMethods.sort((a, b) => methodId(a) - methodId(b))
        }
    })
    if (deliverySetting.orditManagedDelivery === false) {
        ;(deliverySetting.deliveryRules || []).forEach((rule) =>
            setRuleMethods(rule, [RESTAURANT_MANAGED])
        )
    }
    if ('orditManagedDelivery' in deliverySetting) {
        deliverySetting.orditManagedDelivery = true
    }
}

// The day as the windows cover it, sorted: segments (with the rule index), gaps and overlaps,
// all in minutes [from, to] inclusive. A window ending at 23:59 covers the whole last minute.
export function dayCoverage(rules) {
    const segments = (rules || [])
        .map((rule, index) => ({
            index,
            from: toMinutes(rule.timeFrom),
            to: toMinutes(rule.timeTo),
        }))
        .filter((segment) => segment.from !== null && segment.to !== null)
        .sort((a, b) => a.from - b.from || a.to - b.to)

    const gaps = []
    const overlaps = []
    let covered = -1
    let previous = null
    segments.forEach((segment) => {
        // the same window twice (old multi-unit rules) is no overlap, as in core-api's validator
        if (
            previous &&
            previous.from === segment.from &&
            previous.to === segment.to
        ) {
            return
        }
        previous = segment
        if (segment.from > covered + 1) {
            gaps.push({ from: covered + 1, to: segment.from - 1 })
        } else if (segment.from <= covered) {
            overlaps.push({
                from: segment.from,
                to: Math.min(covered, segment.to),
            })
        }
        covered = Math.max(covered, segment.to)
    })
    if (covered < DAY_MINUTES - 1) {
        gaps.push({ from: covered + 1, to: DAY_MINUTES - 1 })
    }

    return {
        segments,
        gaps,
        overlaps,
        invalid: segments.filter((segment) => segment.from >= segment.to),
    }
}

// where "Add time window" puts a new window: the first gap, else the second half of the longest
// window (split on a whole hour where it can), so the day stays covered without overlaps
export function nextWindow(rules) {
    const { segments, gaps } = dayCoverage(rules)
    if (gaps.length) {
        return { from: fromMinutes(gaps[0].from), to: fromMinutes(gaps[0].to) }
    }
    const longest = segments.reduce(
        (best, segment) =>
            !best || segment.to - segment.from > best.to - best.from
                ? segment
                : best,
        null
    )
    if (!longest || longest.to - longest.from < 2) return null
    let split = Math.floor((longest.from + longest.to + 1) / 2)
    const hour = Math.round(split / 60) * 60
    if (hour > longest.from && hour <= longest.to) split = hour
    return {
        splitIndex: longest.index,
        splitTo: fromMinutes(split - 1),
        from: fromMinutes(split),
        to: fromMinutes(longest.to),
    }
}

export function formatRange(from, to) {
    return `${fromMinutes(from)} – ${fromMinutes(to)}`
}
